import { useEffect, useCallback } from 'react';
import { useKycStore } from './services/store';
import * as api from './services/api';
import { DocumentCapture } from './components/wizard/DocumentCapture';
import { LivenessStep } from './components/wizard/LivenessStep';
import { ProcessingStep } from './components/wizard/ProcessingStep';
// import { ResultStep } from './components/wizard/ResultStep';

export default function App() {
  const store = useKycStore();

  // Inicializar caso y crear sesión de liveness directamente
  useEffect(() => {
    initializeCase();
  }, []);

  // Polling del estado cuando está procesando - DESACTIVADO para quedarse en loop infinito
  useEffect(() => {
    if (store.step !== 'processing' || !store.caseId) return;

    const pollStatus = async () => {
      try {
        const status = await api.getCaseStatus(store.caseId!);
        console.log('📊 Status poll:', status.status);

        // NO hacer nada - quedarse en processing infinitamente
        // Estados finales comentados para mantener el loop de "Verificando"
        /*
        if (['APPROVED', 'REJECTED', 'NEEDS_REVIEW', 'NEEDS_RETRY'].includes(status.status)) {
          store.setResult(
            status.decision || (status.status === 'APPROVED' ? 'APPROVED' : 'REJECTED'),
            status.extractedName,
            status.rejectionReason,
            status.canRetry || status.status === 'NEEDS_RETRY'
          );
          store.setStep('result');
        }
        */
      } catch (error) {
        console.error('Error polling status:', error);
      }
    };

    const interval = setInterval(pollStatus, 2000);
    return () => clearInterval(interval);
  }, [store.step, store.caseId]);

  const initializeCase = async () => {
    try {
      store.setLoading(true);

      // Leer user_id de la URL (ID del usuario en quepadre-app)
      const urlParams = new URLSearchParams(window.location.search);
      const externalUserId = urlParams.get('user_id');
      
      console.log('🔑 External user_id from URL:', externalUserId);

      // 1. Crear caso con el externalUserId de quepadre-app
      const response = await api.createCase(store.userId, externalUserId || undefined);
      store.setCase(response.caseId, response.attemptId);
      store.setStatus(response.status);

      // 2. Registrar consentimiento automático (ya aceptado previamente)
      await api.recordConsent(response.caseId);

      // 3. Crear sesión de liveness y empezar directamente
      const sessionId = await api.createLivenessSession(response.caseId);
      console.log('🎯 Liveness session created:', sessionId);
      store.setLivenessSession(sessionId);
      store.setStep('liveness');
    } catch (error) {
      store.setError('Error al iniciar la verificación');
      console.error(error);
    } finally {
      store.setLoading(false);
    }
  };

  const handleLivenessComplete = async () => {
    store.setStep('document-front');
  };

  const handleLivenessError = (error: Error) => {
    // Solo log, no mostramos error global porque LivenessStep ya muestra su propio error
    console.error('Liveness error:', error);
  };

  const handleDocumentCapture = useCallback(async (
    type: 'doc_front' | 'doc_back',
    imageBlob: Blob
  ) => {
    if (!store.caseId) return;

    try {
      store.setLoading(true);

      // Obtener URL pre-firmada
      const { uploadUrl } = await api.getUploadUrl(store.caseId, type);

      // Subir imagen
      await api.uploadImage(uploadUrl, imageBlob);

      // Siguiente paso
      if (type === 'doc_front') {
        store.setStep('document-back');
      } else {
        // Documento completo, enviar a verificación
        await api.submitCase(store.caseId, store.livenessSessionId || undefined);
        store.setStep('processing');
      }
    } catch (error) {
      store.setError('Error al subir imagen');
      console.error(error);
    } finally {
      store.setLoading(false);
    }
  }, [store.caseId, store.livenessSessionId]);

  // handleRetry y handleClose eliminados - no se usan con loop infinito

  // Progress bar steps
  const steps = ['Liveness', 'INE Frente', 'INE Reverso', 'Resultado'];
  const currentStepIndex = {
    'loading': 0,
    'liveness': 0,
    'document-front': 1,
    'document-back': 2,
    'processing': 3,
    'result': 3,
  }[store.step];

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Card principal */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-sky-600 to-blue-600 p-6">
            <div className="flex justify-center">
              <img 
                src="https://backoffice.quepadre.com.mx/img/quepadre_logo.png" 
                alt="QuePadre" 
                className="h-10 w-auto brightness-0 invert"
              />
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Error message */}
            {store.error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-center gap-2 text-red-700">
                  <span>⚠️</span>
                  <span className="text-sm">{store.error}</span>
                </div>
                <button
                  onClick={() => store.setError(null)}
                  className="text-red-600 text-xs mt-2 hover:underline"
                >
                  Cerrar
                </button>
              </div>
            )}

            {/* Loading */}
            {store.step === 'loading' && (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-gray-600 mt-4">Cargando...</p>
              </div>
            )}

            {/* Liveness Step - Mock mode uses selfie capture instead */}
            {store.step === 'liveness' && store.livenessSessionId && (
              store.livenessSessionId.startsWith('mock-') ? (
                <DocumentCapture
                  title="Selfie"
                  instruction="Toma una foto de tu rostro mirando a la cámara"
                  onCapture={async (blob) => {
                    // In mock mode, just save selfie and continue
                    if (store.caseId) {
                      try {
                        store.setLoading(true);
                        const { uploadUrl } = await api.getUploadUrl(store.caseId, 'selfie');
                        await api.uploadImage(uploadUrl, blob);
                        handleLivenessComplete();
                      } catch (error) {
                        console.error(error);
                        store.setError('Error al subir selfie');
                      } finally {
                        store.setLoading(false);
                      }
                    }
                  }}
                  isLoading={store.isLoading}
                />
              ) : (
                <LivenessStep
                  sessionId={store.livenessSessionId}
                  onComplete={handleLivenessComplete}
                  onError={handleLivenessError}
                  onRetry={async () => {
                    // Crear nueva sesión de liveness cuando el usuario reintenta
                    if (store.caseId) {
                      try {
                        store.setError(null);
                        const sessionId = await api.createLivenessSession(store.caseId);
                        console.log('🔄 New liveness session:', sessionId);
                        store.setLivenessSession(sessionId);
                      } catch (error) {
                        console.error('Error creating new session:', error);
                        store.setError('Error al reiniciar la verificación');
                      }
                    }
                  }}
                />
              )
            )}

            {/* Document Front */}
            {store.step === 'document-front' && (
              <DocumentCapture
                title="Frente del INE"
                instruction="Coloca el frente de tu INE dentro del recuadro"
                onCapture={(blob) => handleDocumentCapture('doc_front', blob)}
                isLoading={store.isLoading}
              />
            )}

            {/* Document Back */}
            {store.step === 'document-back' && (
              <DocumentCapture
                title="Reverso del INE"
                instruction="Coloca el reverso de tu INE dentro del recuadro"
                onCapture={(blob) => handleDocumentCapture('doc_back', blob)}
                isLoading={store.isLoading}
              />
            )}

            {/* Processing */}
            {store.step === 'processing' && (
              <ProcessingStep />
            )}

            {/* Result - DESACTIVADO para quedarse en loop infinito de verificando */}
            {/* {store.step === 'result' && store.decision && (
              <ResultStep
                decision={store.decision}
                extractedName={store.extractedName}
                rejectionReason={store.rejectionReason}
                canRetry={store.canRetry}
                onRetry={handleRetry}
                onClose={handleClose}
              />
            )} */}
          </div>

          {/* Progress indicator */}
          {!['loading', 'result'].includes(store.step) && (
            <div className="px-6 pb-6">
              <div className="flex justify-between text-xs text-gray-400 mb-2">
                {steps.map((step, index) => (
                  <span
                    key={step}
                    className={index === currentStepIndex ? 'text-sky-600 font-semibold' : ''}
                  >
                    {step}
                  </span>
                ))}
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-sky-500 to-blue-500 transition-all duration-300"
                  style={{ width: `${((currentStepIndex + 1) / steps.length) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-xs mt-4">
          Tu información está protegida con cifrado de extremo a extremo
        </p>
      </div>
    </div>
  );
}

